import { useSelector } from '@xstate/react';
import { useCallback } from 'react';

import { CheckboxDumb } from '@webapp/common/components/checkbox-dumb';
import { Rating } from '@webapp/common/components/rating';
import { CssQuestionType } from '@webapp/common/components/survey-custom';
import { useMobile } from '@webapp/common/hooks/use-mobile';
import { CANTRATE_ID } from '@webapp/common/resources/constants';
import { PLACEHOLDER } from '@webapp/common/resources/fillers';

import type { SurveyQuestion } from '@webapp/survey/src/components/questions/lib';

import css from './star.css';

export const Star: VFC<SurveyQuestion> = ({
    actor,
    question: {
        params: { amount, color, size, cantRate, cantRateText },
        responseActor,
        id,
    },
    questionBlockPosition,
}) => {
    const { value } = useSelector(responseActor, (r) => r.context);
    const cantRateChecked = value === CANTRATE_ID;

    const isMobile = useMobile();
    const adjustedSize = isMobile ? 1 : size;

    const change = useCallback(
        (value) => {
            responseActor.send({ type: 'CHANGE', value, questionId: id });
            // FIXME: handle RESPONSE_CHANGE in question-machine
            actor.send({ type: 'QUESTION_RESPONSE_CHANGE', value, questionId: id });
        },
        [actor, id, responseActor],
    );

    const handleChecked = useCallback(
        (checked: boolean) => {
            const nextValue = checked ? CANTRATE_ID : '';
            responseActor.send({ type: 'CHANGE', value: nextValue, questionId: id });
            actor.send({ type: 'QUESTION_RESPONSE_CHANGE', value: nextValue, questionId: id });
        },
        [actor, id, responseActor],
    );

    return (
        <div className={css.container}>
            <Rating
                key={String(cantRateChecked)}
                className={CssQuestionType.STARS}
                color={color}
                count={amount}
                defaultValue={cantRateChecked ? 0 : (value as number)}
                isEmpty={cantRateChecked}
                size={adjustedSize}
                onChange={change}
                starsPosition={questionBlockPosition}
            />
            {cantRate && (
                <div>
                    <CheckboxDumb
                        checked={cantRateChecked}
                        className={css.cantRateCheckbox}
                        label={cantRateText || PLACEHOLDER.CANT_RATE}
                        round={false}
                        onChange={handleChecked}
                    />
                </div>
            )}
        </div>
    );
};
Star.displayName = 'Star';
